import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './TemplateEditor.css';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { Save, X, Loader2, Eye, Code, Plus } from 'lucide-react';

const PREDEFINED_TEMPLATES = [
  { value: 'REGISTERATION_CONFIRMATION', label: 'Registration Confirmation' },
  { value: 'CERTIFICATE_CONFIRMATION', label: 'Certificate Confirmation' },
  { value: 'TEAM_INVITE', label: 'Team Invite' },
  { value: 'OTHER', label: 'Other (Custom)' }
];

// Quill editor modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list', 'bullet',
  'color', 'background',
  'align',
  'link'
];

const DEFAULT_TEMPLATES = {
  'REGISTERATION_CONFIRMATION': {
    subject: 'Registration Confirmed for ${eventName}',
    body: `<p>Hello <strong>\${userName}</strong>,</p>
<p>Your registration for the event <strong>\${eventName}</strong> has been successfully confirmed!</p>
<p><strong>Registration ID:</strong> \${userId}<br>
<strong>Event Date:</strong> \${eventDate}</p>
<p>Please find your unique QR code attached.</p>
<p>We look forward to seeing you at the event.</p>
<p>Best regards,<br>
\${eventOrganizerName}<br>
\${eventName} Team</p>`
  },
  'CERTIFICATE_CONFIRMATION': {
    subject: 'Your Certificate for ${eventName} is Ready!',
    body: `<p>Hello <strong>\${name}</strong>,</p>
<p>Congratulations on successfully completing <strong>\${eventName}</strong>!</p>
<p>Please find your <strong>certificate of participation</strong> attached as a PDF document.</p>
<p><strong>Event:</strong> \${eventName}<br>
<strong>Date:</strong> \${eventDate}<br>
<strong>Registration ID:</strong> \${registrationId}</p>
<p>We appreciate your participation and hope to see you in future events.</p>
<p>Best regards,<br>
\${eventOrganizerName}<br>
\${eventName} Team</p>`
  },
  'TEAM_INVITE': {
    subject: 'You\'ve Been Invited to Join the ${eventName} Team!',
    body: `<p>Hello <strong>\${name}</strong>,</p>
<p>You've been invited to join the organizing team for <strong>\${eventName}</strong>.</p>
<p>Here are your login credentials:<br>
<strong>Email:</strong> \${email}<br>
<strong>Password:</strong> \${password}</p>
<p>Welcome aboard and thank you for contributing to the success of \${eventName}.</p>
<p>Best regards,<br>
\${eventOrganizerName}<br>
\${eventName} Team</p>`
  }
};

const TemplateEditorModal = ({ eventId, template, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: template?.name || 'REGISTERATION_CONFIRMATION',
    type: template?.type || 'EMAIL',
    subject: template?.subject || '',
    body: template?.body || '',
  });
  const [variables, setVariables] = useState(
    template?.dynamicVariables
      ? Object.entries(template.dynamicVariables).map(([key, value]) => ({ key, value }))
      : []
  );
  const [showPreview, setShowPreview] = useState(false);
  const [customName, setCustomName] = useState('');
  const { showSuccess, showError } = useToast();

  const isEdit = !!template;

  // Load default template when name changes
  useEffect(() => {
    // Only auto-fill defaults for email templates
    if (!isEdit && formData.type === 'EMAIL') {
      if (formData.name !== 'OTHER' && DEFAULT_TEMPLATES[formData.name]) {
        setFormData(prev => ({
          ...prev,
          ...DEFAULT_TEMPLATES[formData.name]
        }));
      } else if (formData.name === 'OTHER') {
        setFormData(prev => ({
          ...prev,
          subject: '',
          body: ''
        }));
      }
    }
  }, [formData.name, formData.type, isEdit]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => ApiService.createNotificationTemplates(eventId, [data]),
    onSuccess: () => {
      showSuccess('Template created successfully!');
      onSuccess();
    },
    onError: (error) => {
      showError(`Failed to create template: ${error.message}`);
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data) => ApiService.updateNotificationTemplate(eventId, template.externalId, data),
    onSuccess: () => {
      showSuccess('Template updated successfully!');
      onSuccess();
    },
    onError: (error) => {
      showError(`Failed to update template: ${error.message}`);
    }
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVariableChange = (index, field, value) => {
    setVariables(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddVariable = () => {
    setVariables(prev => [...prev, { key: '', value: '' }]);
  };

  const handleRemoveVariable = (index) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    // Validation
    const finalName = formData.name === 'OTHER' ? customName : formData.name;
    
    if (!finalName.trim()) {
      showError('Please enter a template name');
      return;
    }

    if (!formData.subject.trim()) {
      showError('Please enter a subject');
      return;
    }

    if (!formData.body.trim()) {
      showError('Please enter a body');
      return;
    }

    const templateData = {
      name: finalName,
      type: formData.type,
      subject: formData.subject,
      body: formData.body,
      eventId,
      dynamicVariables: variables.reduce((acc, { key, value }) => {
        const trimmedKey = (key || '').trim();
        if (trimmedKey) {
          acc[trimmedKey] = value || '';
        }
        return acc;
      }, {}),
    };

    if (isEdit) {
      updateMutation.mutate(templateData);
    } else {
      createMutation.mutate(templateData);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Code className="w-5 h-5 text-blue-600" />
            <span>{isEdit ? 'Edit Notification Template' : 'Create Notification Template'}</span>
          </DialogTitle>
          <DialogDescription>
            Configure email, WhatsApp, or SMS templates. You can use variables like $&#123;eventName&#125;, $&#123;userName&#125;, etc.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-6">
            {/* Template Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              {isEdit ? (
                <Input
                  id="name"
                  value={formData.name}
                  disabled
                  className="bg-gray-100"
                />
              ) : (
                <>
                  <Select
                    value={formData.name}
                    onValueChange={(value) => handleInputChange('name', value)}
                  >
                    <SelectTrigger id="name">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREDEFINED_TEMPLATES.map((tmpl) => (
                        <SelectItem key={tmpl.value} value={tmpl.value}>
                          {tmpl.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                
                  {formData.name === 'OTHER' && (
                    <Input
                      placeholder="Enter custom template name"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      className="mt-2"
                    />
                  )}
                </>
              )}
            </div>

            {/* Channel / Type */}
            <div className="space-y-2">
              <Label htmlFor="type">Channel *</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => handleInputChange('type', value)}
              >
                <SelectTrigger id="type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                  <SelectItem value="SMS">SMS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Subject */}
            <div className="space-y-2">
              <Label htmlFor="subject">Subject *</Label>
              <Input
                id="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="e.g., Welcome to ${eventName}"
              />
            </div>

            {/* Body */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="body">Body *</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowPreview(!showPreview)}
                  type="button"
                >
                  <Eye className="w-4 h-4 mr-1" />
                  {showPreview ? 'Hide' : 'Show'} Preview
                </Button>
              </div>
              <div className="border rounded-lg overflow-hidden template-editor-quill">
                <ReactQuill
                  theme="snow"
                  value={formData.body}
                  onChange={(value) => handleInputChange('body', value)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Enter your message body. Use the toolbar for formatting and variables like ${eventName}, ${userName}, etc."
                />
              </div>
              <p className="text-xs text-gray-500">
                Tip: Use variables like $&#123;eventName&#125;, $&#123;userName&#125;, $&#123;email&#125;, etc. in your template.
              </p>
            </div>

            {/* Dynamic Variables */}
            <div className="space-y-2">
              <Label>Dynamic Variables (optional)</Label>
              <p className="text-sm text-gray-600">
                Define extra key/value variables that can be referenced in this template. These are especially useful for WhatsApp and SMS templates.
              </p>
              <div className="space-y-2">
                {variables.map((variable, index) => (
                  <div key={index} className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="Variable key (e.g. otpCode)"
                      value={variable.key}
                      onChange={(e) => handleVariableChange(index, 'key', e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Input
                        placeholder="Description or example value"
                        value={variable.value}
                        onChange={(e) => handleVariableChange(index, 'value', e.target.value)}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleRemoveVariable(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddVariable}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Variable
                </Button>
              </div>
            </div>

            {/* Preview */}
            {showPreview && (
              <div className="space-y-2">
                <Label>Preview (with sample data)</Label>
                <div className="border rounded-lg p-6 bg-gray-50">
                  <div className="bg-white rounded-lg shadow-sm p-6">
                    <p className="text-sm font-semibold text-gray-700 mb-4 pb-4 border-b">
                      Subject: {formData.subject
                        .replace(/\$\{eventName\}/g, 'Sample Event')
                        .replace(/\$\{userName\}/g, 'John Doe')
                        .replace(/\$\{name\}/g, 'John Doe')}
                    </p>
                    <div
                      className="text-sm text-gray-800"
                      dangerouslySetInnerHTML={{
                        __html: formData.body
                          .replace(/\$\{eventName\}/g, 'Sample Event')
                          .replace(/\$\{userName\}/g, 'John Doe')
                          .replace(/\$\{name\}/g, 'John Doe')
                          .replace(/\$\{userId\}/g, 'USER-12345')
                          .replace(/\$\{eventDate\}/g, 'Dec 25, 2025')
                          .replace(/\$\{eventOrganizerName\}/g, 'Event Organizer')
                          .replace(/\$\{email\}/g, 'john@example.com')
                          .replace(/\$\{password\}/g, '********')
                          .replace(/\$\{registrationId\}/g, 'REG-12345')
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="flex space-x-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isPending}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                <span>{isEdit ? 'Updating...' : 'Creating...'}</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                <span>{isEdit ? 'Update Template' : 'Create Template'}</span>
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateEditorModal;

